import { useEffect, useRef, useState } from "react";

/**
 * The result returned by a single invocation of a {@link PollFn}.
 *
 * @template T - The shape of the data payload returned by the poll function.
 */
export type PollResult<T> = {
  /** Optional data payload returned by this poll cycle. */
  data?: T;
  /**
   * Controls whether polling should continue after this result.
   *
   * `true`  — polling terminates; the operation has reached completion.
   * `false` — polling continues after the next interval delay.
   */
  complete: boolean;
};

/**
 * A client-supplied function that performs a single async fetch and returns
 * a {@link PollResult}. Called once per poll cycle.
 *
 * @template T - The shape of the data payload.
 */
export type PollFn<T> = () => Promise<PollResult<T>>;

/**
 * Configuration options for {@link useIntervalPolling}.
 */
export type UsePollingOptions = {
  /**
   * The delay (in milliseconds) between the *end* of one fetch and the *start* of the next.
   * This guarantees fetches never overlap regardless of how long each request takes.
   */
  intervalTime: number;

  /**
   * If `true`, polling begins immediately when the hook mounts.
   * If `false` (default), polling only begins when `controls.start()` is called explicitly.
   */
  startImmediate?: boolean;
};

/**
 * A snapshot of the current polling state, returned as the first element of
 * the {@link useIntervalPolling} tuple.
 *
 * @template T - The shape of the data payload.
 */
export type UsePollingState<T> = {
  /** `true` while the polling loop is active (between `start()` and `stop()`). */
  isPolling: boolean;
  /** `true` while a single poll fetch is currently in-flight. */
  isRunningOnce: boolean;
  /** The most recently received data payload. `undefined` until the first successful fetch. */
  lastData?: T;
  /** The error thrown by the most recent failed fetch. Cleared on the next successful fetch. */
  error?: unknown;
};

/**
 * Imperative controls returned as the second element of the {@link useIntervalPolling} tuple.
 */
export type UsePollingControls = {
  /**
   * Starts the polling loop. No-op if polling is already active.
   * Sets `isPolling: true`, which triggers the core polling effect.
   */
  start: () => void;
  /**
   * Stops the polling loop immediately. Cancels any pending interval timer and
   * orphans any in-flight fetch (its result will be silently discarded).
   */
  stop: () => void;
};

/**
 * React hook that repeatedly calls an async function at a fixed interval,
 * exposing the latest result and controls to start and stop the loop.
 *
 * ## Architecture
 * The polling loop is driven entirely by React's `useEffect` state machine
 * rather than recursive `setTimeout` calls. Each poll cycle runs inside its
 * own isolated effect scope, which means:
 * - The JS async call-stack stays flat — no exponentially growing stack traces.
 * - Cleanup is handled automatically by React's effect teardown on every cycle.
 * - Unmounting or calling `stop()` synchronously sets `isCancelled`, preventing
 *   any in-flight promise callbacks from scheduling further work.
 *
 * ## Usage
 * ```tsx
 * const [state, controls] = useIntervalPolling(fetchStatus, {
 *   intervalTime: 5000,
 *   startImmediate: true,
 * });
 * ```
 *
 * @template T - The shape of the data payload returned by `asyncClientPoll`.
 * @param asyncClientPoll - Client-supplied function called once per poll cycle.
 *   Must return a {@link PollResult}. Return `{ complete: true }` to self-terminate
 *   the loop when the desired condition is met.
 * @param options - {@link UsePollingOptions} controlling interval timing and auto-start.
 * @returns A tuple of `[state, controls]` — see {@link UsePollingState} and {@link UsePollingControls}.
 */
export function useIntervalPolling<T>(
  asyncClientPoll: PollFn<T>,
  options: UsePollingOptions
): [UsePollingState<T>, UsePollingControls] {
  const { intervalTime, startImmediate = false } = options;

  const [state, setState] = useState<UsePollingState<T>>({
    isPolling: false,
    isRunningOnce: false,
    lastData: undefined,
    error: undefined
  });

  // Incremented after each completed fetch to re-trigger the polling effect without recursion.
  const [tick, setTick] = useState(0);

  // Holds the latest poll function so the effect always calls the current version
  // without needing to be listed as a dependency (which would restart the loop on every render).
  const pollFnRef = useRef<PollFn<T>>(asyncClientPoll);

  // Shared cancellation flag. Set synchronously by stop() and by effect cleanup so that
  // any in-flight .then()/.catch()/.finally() callbacks can bail out immediately.
  const isCancelled = useRef<boolean>(false);

  // Keep the poll function ref in sync with the latest prop without triggering re-renders.
  useEffect(() => {
    pollFnRef.current = asyncClientPoll;
  }, [asyncClientPoll]);

  // Stable controls object. Defined in a ref so consumers receive the same object
  // reference on every render, preventing unnecessary downstream re-renders.
  const controls = useRef<UsePollingControls>({
    stop: () => {
      // Synchronously mark cancelled so any in-flight promise callbacks abort immediately,
      // before React has had a chance to flush the state update below.
      isCancelled.current = true;
      setState((prev) => {
        if (!prev.isPolling && !prev.isRunningOnce) return prev;
        return {
          ...prev,
          isPolling: false,
          isRunningOnce: false
        };
      });
    },
    start: () => {
      // Reset the cancellation flag before starting so the next effect cycle is permitted to run.
      isCancelled.current = false;
      setState((prev) => {
        if (prev.isPolling) return prev;
        return {
          ...prev,
          isPolling: true,
          error: undefined
        };
      });
    }
  });

  // ---------------------------------------------------------------------------
  // Core Polling Loop
  // ---------------------------------------------------------------------------
  // Each time `state.isPolling`, `tick`, or `intervalTime` changes, React runs
  // this effect in a brand-new scope and tears down the previous one. This gives
  // us automatic cancellation and a completely flat async call-stack — there are
  // no nested closures and no recursive setTimeouts.
  useEffect(() => {
    if (!state.isPolling) {
      return () => {};
    }

    let timerId: ReturnType<typeof setTimeout> | undefined = undefined;

    // Capture stop() at the start of this effect scope so it doesn't need to be
    // in the dependency array (controls.current is a mutable ref, not tracked by React).
    const stop = controls.current.stop;

    const executePoll = () => {
      if (isCancelled.current) return;

      setState((prev) => ({ ...prev, isRunningOnce: true, error: undefined }));

      // Use Promise.resolve().then(() => fn()) rather than Promise.resolve(fn()) so that
      // any synchronous throw inside pollFnRef.current() is captured by the .catch() below.
      Promise.resolve()
        .then(() => pollFnRef.current())
        .then((result) => {
          // Discard the result if stop() was called or the component unmounted while fetching.
          if (isCancelled.current) return;

          // Use Object.hasOwn() rather than truthiness so falsy data values (0, false, "")
          // are correctly captured in state.
          if (result && Object.hasOwn(result, "data")) {
            setState((prev) => ({ ...prev, lastData: result.data }));
          }

          if (result?.complete) {
            // The poll function signals it is done — stop the loop.
            stop();
          } else {
            // Schedule the next cycle. Using setTimeout here (rather than calling executePoll
            // directly) ensures the interval is measured from the *end* of the fetch, not the
            // start, so slow requests never cause overlapping calls.
            timerId = setTimeout(() => {
              if (!isCancelled.current) {
                // Incrementing tick re-triggers this effect in a fresh scope, keeping the
                // call-stack flat across every poll cycle.
                setTick((t) => t + 1);
              }
            }, intervalTime);
          }
        })
        .catch((err) => {
          if (isCancelled.current) return;

          setState((prev) => ({ ...prev, error: err }));
          stop(); // Stop the loop on error by default.
        })
        .finally(() => {
          if (!isCancelled.current) {
            setState((prev) => ({ ...prev, isRunningOnce: false }));
          }
        });
    };

    executePoll();

    // Cleanup:
    // React calls when component unmounts or when `state.isPolling` becomes false,
    // or when `tick` or `intervalTime` change to start the next cycle.
    // Setting `isCancelled` to true when there is no ongoing polling prevents the previous cycle's in-flight promise from scheduling further work.
    return () => {
      if (!state.isPolling) {
        isCancelled.current = true;
      }
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [state.isPolling, tick, intervalTime]);

  // Auto-start on mount when startImmediate is true.
  useEffect(() => {
    if (startImmediate) {
      controls.current.start();
    }
  }, [startImmediate]);

  return [state, controls.current];
}
